import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Glassmorphism Settings - Configurações customizáveis
 */
export interface GlassmorphismSettings {
  blur: number;        // 0-30px (padrão: 15)
  opacity: number;     // 0-100% (padrão: 35)
  saturation: number;  // 100-300% (padrão: 180)
}

export const DEFAULT_GLASSMORPHISM: GlassmorphismSettings = {
  blur: 15,
  opacity: 35,
  saturation: 180,
};

const GLASSMORPHISM_KEY = 'd12-glassmorphism-settings';

/**
 * Background Preset Types
 */
export type BackgroundPreset = 'default' | 'minimal' | 'cyberpunk' | 'aurora' | 'deepSpace' | 'custom';

/**
 * Background Settings - Configurações customizáveis do background animado
 */
export interface BackgroundSettings {
  // Preset atual
  preset: BackgroundPreset;

  // Cor de fundo base
  baseColor: string;         // Cor de fundo (#09090b padrão)

  // Cores das nebulosas (tons de roxo/índigo)
  nebulaColor1: string;      // Cor primária (roxo profundo)
  nebulaColor2: string;      // Cor secundária (índigo)
  nebulaIntensity: number;   // 0-150% intensidade/opacidade das nebulosas
  nebulaBlur: number;        // 50-150px blur das nebulosas
  nebulaSize: number;        // 50-150% tamanho das nebulosas

  // Partículas
  particleCount: number;     // 30-300 partículas
  particleSpeed: number;     // 0.1-1.0 velocidade
  particleOpacity: number;   // 5-50% opacidade
  particleColor: string;     // Cor das partículas
  particleSize: number;      // 0.5-3.0 tamanho das partículas
  lineOpacity: number;       // 0-30% opacidade das linhas
  lineRange: number;         // 50-200 range de conexão das linhas

  // Animação
  animationSpeed: number;    // 0.3-2.0 multiplicador de velocidade
  noiseOpacity: number;      // 0-5% opacidade do ruído
  pauseAnimations: boolean;  // Pausar todas as animações

  // Toggle
  enableNebulas: boolean;    // Habilitar nebulosas
  enableParticles: boolean;  // Habilitar partículas
}

export const DEFAULT_BACKGROUND: BackgroundSettings = {
  // Preset
  preset: 'default',

  // Cor de fundo base
  baseColor: '#09090b',

  // Cores padrão (roxo/índigo)
  nebulaColor1: '#581c87',   // purple-900
  nebulaColor2: '#4338ca',   // indigo-700
  nebulaIntensity: 100,
  nebulaBlur: 100,
  nebulaSize: 100,

  // Partículas padrão
  particleCount: 150,
  particleSpeed: 0.2,
  particleOpacity: 15,
  particleColor: '#8b5cf6',  // violet-500
  particleSize: 1.2,
  lineOpacity: 5,
  lineRange: 100,

  // Animação padrão
  animationSpeed: 1.0,
  noiseOpacity: 1.2,
  pauseAnimations: false,

  // Toggles padrão
  enableNebulas: true,
  enableParticles: true,
};

// Background Presets - Configurações completas prontas para uso
export const BACKGROUND_PRESETS: Record<Exclude<BackgroundPreset, 'custom'>, Omit<BackgroundSettings, 'preset'>> = {
  default: {
    baseColor: '#09090b',
    nebulaColor1: '#581c87',
    nebulaColor2: '#4338ca',
    nebulaIntensity: 100,
    nebulaBlur: 100,
    nebulaSize: 100,
    particleCount: 150,
    particleSpeed: 0.2,
    particleOpacity: 15,
    particleColor: '#8b5cf6',
    particleSize: 1.2,
    lineOpacity: 5,
    lineRange: 100,
    animationSpeed: 1.0,
    noiseOpacity: 1.2,
    pauseAnimations: false,
    enableNebulas: true,
    enableParticles: true,
  },
  minimal: {
    baseColor: '#0a0a0a',
    nebulaColor1: '#18181b',
    nebulaColor2: '#27272a',
    nebulaIntensity: 30,
    nebulaBlur: 120,
    nebulaSize: 80,
    particleCount: 50,
    particleSpeed: 0.1,
    particleOpacity: 8,
    particleColor: '#52525b',
    particleSize: 0.8,
    lineOpacity: 2,
    lineRange: 80,
    animationSpeed: 0.5,
    noiseOpacity: 0.5,
    pauseAnimations: false,
    enableNebulas: true,
    enableParticles: true,
  },
  cyberpunk: {
    baseColor: '#030712',
    nebulaColor1: '#be185d',
    nebulaColor2: '#0891b2',
    nebulaIntensity: 120,
    nebulaBlur: 80,
    nebulaSize: 110,
    particleCount: 200,
    particleSpeed: 0.4,
    particleOpacity: 25,
    particleColor: '#22d3ee',
    particleSize: 1.5,
    lineOpacity: 10,
    lineRange: 120,
    animationSpeed: 1.5,
    noiseOpacity: 2.5,
    pauseAnimations: false,
    enableNebulas: true,
    enableParticles: true,
  },
  aurora: {
    baseColor: '#020617',
    nebulaColor1: '#059669',
    nebulaColor2: '#0284c7',
    nebulaIntensity: 90,
    nebulaBlur: 110,
    nebulaSize: 120,
    particleCount: 100,
    particleSpeed: 0.15,
    particleOpacity: 12,
    particleColor: '#34d399',
    particleSize: 1.0,
    lineOpacity: 4,
    lineRange: 90,
    animationSpeed: 0.7,
    noiseOpacity: 1.0,
    pauseAnimations: false,
    enableNebulas: true,
    enableParticles: true,
  },
  deepSpace: {
    baseColor: '#000000',
    nebulaColor1: '#1e1b4b',
    nebulaColor2: '#312e81',
    nebulaIntensity: 60,
    nebulaBlur: 130,
    nebulaSize: 140,
    particleCount: 250,
    particleSpeed: 0.08,
    particleOpacity: 30,
    particleColor: '#e2e8f0',
    particleSize: 0.6,
    lineOpacity: 0,
    lineRange: 50,
    animationSpeed: 0.4,
    noiseOpacity: 0.8,
    pauseAnimations: false,
    enableNebulas: true,
    enableParticles: true,
  },
};

export const PRESET_METADATA: Record<Exclude<BackgroundPreset, 'custom'>, { label: string; description: string }> = {
  default: { label: 'Padrão', description: 'Visual elegante roxo/índigo' },
  minimal: { label: 'Minimal', description: 'Sutil e discreto' },
  cyberpunk: { label: 'Cyberpunk', description: 'Neon vibrante rosa/ciano' },
  aurora: { label: 'Aurora', description: 'Verde/azul relaxante' },
  deepSpace: { label: 'Deep Space', description: 'Estrelas no espaço profundo' },
};

const BACKGROUND_KEY = 'd12-background-settings';

/**
 * Card Style Types - Temas dark mode disponíveis
 *
 * Pretos:
 * - 'obsidian': Preto profundo (padrão)
 * - 'void': Preto absoluto (true black)
 *
 * Cinzas:
 * - 'charcoal': Cinza carvão neutro
 * - 'steel': Cinza aço frio
 * - 'slate': Cinza ardósia
 *
 * Azuis:
 * - 'midnight': Azul meia-noite corporativo
 * - 'navy': Azul marinho profundo
 * - 'ocean': Azul oceano
 *
 * Roxos:
 * - 'purple': Roxo escuro elegante
 * - 'violet': Violeta profundo
 *
 * Especiais:
 * - 'glass': Glassmorphism com blur
 * - 'warm': Tons âmbar para noite
 */
export type CardStyle =
  | 'obsidian' | 'void'           // Pretos
  | 'charcoal' | 'steel' | 'slate' // Cinzas
  | 'midnight' | 'navy' | 'ocean'  // Azuis
  | 'purple' | 'violet'            // Roxos
  | 'glass' | 'warm';              // Especiais

// Ordem dos temas para ciclo (agrupado por categoria)
const THEME_ORDER: CardStyle[] = [
  'obsidian', 'void',           // Pretos
  'charcoal', 'steel', 'slate', // Cinzas
  'midnight', 'navy', 'ocean',  // Azuis
  'purple', 'violet',           // Roxos
  'glass', 'warm'               // Especiais
];

// Categorias de temas para o seletor
export const THEME_CATEGORIES = {
  blacks: ['obsidian', 'void'] as CardStyle[],
  grays: ['charcoal', 'steel', 'slate'] as CardStyle[],
  blues: ['midnight', 'navy', 'ocean'] as CardStyle[],
  purples: ['purple', 'violet'] as CardStyle[],
  special: ['glass', 'warm'] as CardStyle[],
};

export const THEME_CATEGORY_LABELS: Record<string, string> = {
  blacks: 'Pretos',
  grays: 'Cinzas',
  blues: 'Azuis',
  purples: 'Roxos',
  special: 'Especiais',
};

// Metadados completos dos temas
export interface ThemeMetadata {
  label: string;
  description: string;
  previewColor: string; // Cor de preview para o seletor
  accentColor: string;  // Cor de destaque
}

export const THEME_METADATA: Record<CardStyle, ThemeMetadata> = {
  // Pretos
  obsidian: {
    label: 'Obsidian',
    description: 'Preto profundo elegante',
    previewColor: '#050506',
    accentColor: '#8b5cf6',
  },
  void: {
    label: 'Void',
    description: 'Preto absoluto AMOLED',
    previewColor: '#000000',
    accentColor: '#a78bfa',
  },
  // Cinzas
  charcoal: {
    label: 'Charcoal',
    description: 'Cinza carvão neutro',
    previewColor: '#1e1e1e',
    accentColor: '#8b5cf6',
  },
  steel: {
    label: 'Steel',
    description: 'Cinza aço frio',
    previewColor: '#1c1f26',
    accentColor: '#64748b',
  },
  slate: {
    label: 'Slate',
    description: 'Cinza ardósia suave',
    previewColor: '#1e293b',
    accentColor: '#94a3b8',
  },
  // Azuis
  midnight: {
    label: 'Midnight Blue',
    description: 'Azul meia-noite corporativo',
    previewColor: '#0f1f3d',
    accentColor: '#3b82f6',
  },
  navy: {
    label: 'Navy',
    description: 'Azul marinho profundo',
    previewColor: '#0a1929',
    accentColor: '#1e88e5',
  },
  ocean: {
    label: 'Ocean',
    description: 'Azul oceano sereno',
    previewColor: '#0d2137',
    accentColor: '#06b6d4',
  },
  // Roxos
  purple: {
    label: 'Deep Purple',
    description: 'Roxo escuro elegante',
    previewColor: '#1a1025',
    accentColor: '#9333ea',
  },
  violet: {
    label: 'Violet',
    description: 'Violeta profundo místico',
    previewColor: '#1e1033',
    accentColor: '#a855f7',
  },
  // Especiais
  glass: {
    label: 'Glass',
    description: 'Glassmorphism com blur',
    previewColor: '#141419',
    accentColor: '#8b5cf6',
  },
  warm: {
    label: 'Warm Night',
    description: 'Tons âmbar confortáveis',
    previewColor: '#2a1f18',
    accentColor: '#f59e0b',
  },
};

// Labels simples para compatibilidade
export const THEME_LABELS: Record<CardStyle, string> = Object.fromEntries(
  Object.entries(THEME_METADATA).map(([key, meta]) => [key, meta.label])
) as Record<CardStyle, string>;

interface ThemeContextType {
  theme: 'dark';
  cardStyle: CardStyle;
  setCardStyle: (style: CardStyle) => void;
  toggleCardStyle: () => void;
  allThemes: CardStyle[];
  // Glassmorphism settings
  glassmorphism: GlassmorphismSettings;
  setGlassmorphism: (settings: GlassmorphismSettings) => void;
  updateGlassmorphism: (key: keyof GlassmorphismSettings, value: number) => void;
  resetGlassmorphism: () => void;
  // Background settings
  background: BackgroundSettings;
  setBackground: (settings: BackgroundSettings) => void;
  updateBackground: <K extends keyof BackgroundSettings>(key: K, value: BackgroundSettings[K]) => void;
  resetBackground: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const CARD_STYLE_KEY = 'd12-card-style';

export function ThemeProvider({
  children,
  ...props
}: {
  children: React.ReactNode;
}) {
  // Load saved card style from localStorage or default to 'obsidian'
  const [cardStyle, setCardStyleState] = useState<CardStyle>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(CARD_STYLE_KEY);
      if (saved && THEME_ORDER.includes(saved as CardStyle)) {
        return saved as CardStyle;
      }
    }
    return 'obsidian';
  });

  // Load saved glassmorphism settings from localStorage
  const [glassmorphism, setGlassmorphismState] = useState<GlassmorphismSettings>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(GLASSMORPHISM_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return { ...DEFAULT_GLASSMORPHISM, ...parsed };
        } catch {
          return DEFAULT_GLASSMORPHISM;
        }
      }
    }
    return DEFAULT_GLASSMORPHISM;
  });

  // Load saved background settings from localStorage
  const [background, setBackgroundState] = useState<BackgroundSettings>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(BACKGROUND_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return { ...DEFAULT_BACKGROUND, ...parsed };
        } catch {
          return DEFAULT_BACKGROUND;
        }
      }
    }
    return DEFAULT_BACKGROUND;
  });

  useEffect(() => {
    // Always set dark mode on mount
    const root = window.document.documentElement;
    root.classList.add('dark');

    // Clean up any old theme preferences from localStorage
    localStorage.removeItem('vite-ui-theme');
    localStorage.removeItem('vite-ui-theme-tint');
  }, []);

  // Apply card style class to document
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove all theme classes first
    THEME_ORDER.forEach(theme => {
      root.classList.remove(`card-style-${theme}`);
    });

    // Add current style class
    root.classList.add(`card-style-${cardStyle}`);

    // Save to localStorage
    localStorage.setItem(CARD_STYLE_KEY, cardStyle);
  }, [cardStyle]);

  const setCardStyle = (style: CardStyle) => {
    setCardStyleState(style);
  };

  const toggleCardStyle = () => {
    setCardStyleState(prev => {
      const currentIndex = THEME_ORDER.indexOf(prev);
      const nextIndex = (currentIndex + 1) % THEME_ORDER.length;
      return THEME_ORDER[nextIndex];
    });
  };

  // Glassmorphism methods
  const setGlassmorphism = useCallback((settings: GlassmorphismSettings) => {
    setGlassmorphismState(settings);
    localStorage.setItem(GLASSMORPHISM_KEY, JSON.stringify(settings));
  }, []);

  const updateGlassmorphism = useCallback((key: keyof GlassmorphismSettings, value: number) => {
    setGlassmorphismState(prev => {
      const newSettings = { ...prev, [key]: value };
      localStorage.setItem(GLASSMORPHISM_KEY, JSON.stringify(newSettings));
      return newSettings;
    });
  }, []);

  const resetGlassmorphism = useCallback(() => {
    setGlassmorphismState(DEFAULT_GLASSMORPHISM);
    localStorage.setItem(GLASSMORPHISM_KEY, JSON.stringify(DEFAULT_GLASSMORPHISM));
  }, []);

  // Background methods
  const setBackground = useCallback((settings: BackgroundSettings) => {
    setBackgroundState(settings);
    localStorage.setItem(BACKGROUND_KEY, JSON.stringify(settings));
  }, []);

  const updateBackground = useCallback(<K extends keyof BackgroundSettings>(key: K, value: BackgroundSettings[K]) => {
    setBackgroundState(prev => {
      const newSettings = { ...prev, [key]: value };
      localStorage.setItem(BACKGROUND_KEY, JSON.stringify(newSettings));
      return newSettings;
    });
  }, []);

  const resetBackground = useCallback(() => {
    setBackgroundState(DEFAULT_BACKGROUND);
    localStorage.setItem(BACKGROUND_KEY, JSON.stringify(DEFAULT_BACKGROUND));
  }, []);

  const value = {
    theme: 'dark' as const,
    cardStyle,
    setCardStyle,
    toggleCardStyle,
    allThemes: THEME_ORDER,
    // Glassmorphism
    glassmorphism,
    setGlassmorphism,
    updateGlassmorphism,
    resetGlassmorphism,
    // Background
    background,
    setBackground,
    updateBackground,
    resetBackground,
  };

  return (
    <ThemeContext.Provider {...props} value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Hook seguro que retorna valores padrão se o ThemeProvider não estiver disponível.
 * Usado internamente para componentes que podem ser renderizados fora do provider.
 */
function useThemeSafe() {
  const context = useContext(ThemeContext);
  return context;
}

/**
 * Hook para obter as classes/styles de card baseado no tema atual.
 * Retorna valores padrão seguros se usado fora do ThemeProvider.
 */
export function useCardStyle() {
  const context = useThemeSafe();

  // Valores padrão se contexto não disponível
  const cardStyle = context?.cardStyle ?? 'obsidian';
  const glassmorphism = context?.glassmorphism ?? DEFAULT_GLASSMORPHISM;

  // Configurações de background por tema
  const getCardClassName = (): string => {
    switch (cardStyle) {
      // Pretos
      case 'obsidian':
        return 'bg-[#050506]/85';
      case 'void':
        return 'bg-black/95';
      // Cinzas
      case 'charcoal':
        return 'bg-[#1e1e1e]/90';
      case 'steel':
        return 'bg-[#1c1f26]/90';
      case 'slate':
        return 'bg-[#1e293b]/90';
      // Azuis
      case 'midnight':
        return 'bg-[#0f1f3d]/90';
      case 'navy':
        return 'bg-[#0a1929]/90';
      case 'ocean':
        return 'bg-[#0d2137]/90';
      // Roxos
      case 'purple':
        return 'bg-[#1a1025]/90';
      case 'violet':
        return 'bg-[#1e1033]/90';
      // Especiais
      case 'glass':
        return 'card-glass';
      case 'warm':
        return 'bg-[#2a1f18]/90';
      default:
        return 'bg-[#050506]/85';
    }
  };

  // Configurações de borda por tema
  const getBorderClassName = (): string => {
    switch (cardStyle) {
      case 'obsidian':
      case 'void':
        return 'border-white/5';
      case 'charcoal':
        return 'border-white/[0.08]';
      case 'steel':
        return 'border-slate-500/10';
      case 'slate':
        return 'border-slate-400/10';
      case 'midnight':
      case 'navy':
        return 'border-blue-400/10';
      case 'ocean':
        return 'border-cyan-400/10';
      case 'purple':
      case 'violet':
        return 'border-purple-400/10';
      case 'glass':
        return 'border-white/[0.06]';
      case 'warm':
        return 'border-amber-500/10';
      default:
        return 'border-white/5';
    }
  };

  // Style object para cards (inline styles quando necessário)
  // Usa os mesmos valores do sidebar/header para manter consistência
  // Todos os temas agora incluem glassmorphism (backdrop-filter blur)
  // Valores dinâmicos vêm das configurações de glassmorphism
  const getCardStyleObject = (): React.CSSProperties | undefined => {
    const { blur, opacity, saturation } = glassmorphism;
    const opacityDecimal = opacity / 100;

    // Base blur style para glassmorphism - valores dinâmicos
    const baseStyle: React.CSSProperties = {
      backdropFilter: `blur(${blur}px) saturate(${saturation}%)`,
      WebkitBackdropFilter: `blur(${blur}px) saturate(${saturation}%)`,
    };

    // Cores base de cada tema (RGB sem alpha)
    const themeColors: Record<CardStyle, string> = {
      obsidian: '0, 0, 0',
      void: '0, 0, 0',
      charcoal: '18, 18, 18',
      steel: '28, 31, 38',
      slate: '30, 41, 59',
      midnight: '15, 31, 61',
      navy: '10, 25, 41',
      ocean: '13, 33, 55',
      purple: '26, 16, 37',
      violet: '30, 16, 51',
      glass: '20, 20, 25',
      warm: '42, 31, 24',
    };

    const rgb = themeColors[cardStyle] || '0, 0, 0';
    return { ...baseStyle, background: `rgba(${rgb}, ${opacityDecimal})` };
  };

  // Style object para dropdowns/popovers
  // Todos os temas agora incluem glassmorphism
  // Popovers usam blur menor e opacidade maior para legibilidade
  const getPopoverStyleObject = (): React.CSSProperties => {
    const { blur, saturation } = glassmorphism;
    const popoverBlur = Math.max(blur - 3, 8); // Blur um pouco menor para popovers

    const blurStyle = {
      backdropFilter: `blur(${popoverBlur}px) saturate(${saturation}%)`,
      WebkitBackdropFilter: `blur(${popoverBlur}px) saturate(${saturation}%)`,
    };

    // Cores mais opacas para popovers (0.95-0.98) para melhor legibilidade
    const popoverColors: Record<CardStyle, string> = {
      glass: 'rgba(20, 20, 25, 0.95)',
      void: 'rgba(0, 0, 0, 0.98)',
      obsidian: 'rgba(5, 5, 6, 0.95)',
      charcoal: 'rgba(18, 18, 18, 0.98)',
      steel: 'rgba(20, 23, 28, 0.98)',
      slate: 'rgba(15, 23, 42, 0.98)',
      midnight: 'rgba(10, 22, 40, 0.98)',
      navy: 'rgba(6, 17, 30, 0.98)',
      ocean: 'rgba(8, 24, 40, 0.98)',
      purple: 'rgba(18, 10, 28, 0.98)',
      violet: 'rgba(22, 10, 38, 0.98)',
      warm: 'rgba(26, 20, 16, 0.98)',
    };

    return { background: popoverColors[cardStyle] || 'rgba(5, 5, 6, 0.95)', ...blurStyle };
  };

  const metadata = THEME_METADATA[cardStyle];

  return {
    cardStyle,
    glassmorphism,
    themeLabel: metadata.label,
    themeDescription: metadata.description,
    accentColor: metadata.accentColor,
    previewColor: metadata.previewColor,
    isObsidian: cardStyle === 'obsidian',
    isGlass: cardStyle === 'glass',
    isMidnight: cardStyle === 'midnight',
    isCharcoal: cardStyle === 'charcoal',
    isWarm: cardStyle === 'warm',
    cardClassName: getCardClassName(),
    borderClassName: getBorderClassName(),
    cardStyleObject: getCardStyleObject(),
    popoverStyleObject: getPopoverStyleObject(),
  };
}
